// Models
// Utilities
import * as StringHelper from "@fluentui/utilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import isEmpty from "lodash/isEmpty";
/**
 * Implementation for ISensitivityLabel
 */ export class SensitivityLabel {
    static buildFromGraphResource(sensitivityLabelResource) {
        const sublabels = sensitivityLabelResource.sublabels?.map((sublabel)=>this.buildFromGraphResource(sublabel));
        let groupAllowGuestUsers = true;
        let groupPrivacy = "";
        const labelActions = sensitivityLabelResource.labelActions;
        if (labelActions && !isEmpty(labelActions)) {
            for (const labelAction of labelActions){
                if (labelAction.name === "protectGroup") {
                    // for now, we use the most restrictive default until we know any different. Bug: 5932482
                    groupAllowGuestUsers = labelAction.allowGuestUsers ?? false;
                    groupPrivacy = labelAction.privacy ?? "";
                    break;
                }
            }
        }
        let hasProtection = false;
        // Check if this label has protection (contains "encryptedWith" in labelActions)
        if (labelActions && !isEmpty(labelActions)) {
            for (const labelAction of labelActions){
                if ("encryptWith" in labelAction && labelAction.isEnabled) {
                    hasProtection = true;
                }
            }
        }
        // Also check sublabels for protection
        if (sublabels && !hasProtection) {
            for (const sublabel of sublabels){
                if (sublabel.hasProtection) {
                    hasProtection = true;
                    break;
                }
            }
        }
        return new SensitivityLabel({
            id: sensitivityLabelResource.id,
            name: sensitivityLabelResource.name,
            displayName: sensitivityLabelResource.displayName,
            description: sensitivityLabelResource.description,
            toolTip: sensitivityLabelResource.toolTip,
            isDefault: sensitivityLabelResource.isDefault,
            isEnabled: sensitivityLabelResource.isEnabled,
            priority: sensitivityLabelResource.priority,
            color: sensitivityLabelResource.color ?? "",
            hasProtection: hasProtection,
            labelActions: sensitivityLabelResource.labelActions ?? [],
            groupAllowGuestUsers: groupAllowGuestUsers,
            groupPrivacy: groupPrivacy,
            sublabels: sublabels ?? null,
            defaultContentLabel: sensitivityLabelResource.defaultContentLabel ? this.buildFromGraphResource(sensitivityLabelResource.defaultContentLabel) : null
        });
    }
    /**
     * Given a list of sensitivity labels, return the one that's default.
     * If none of them is default, return null.
     * @param sensitivityLabels sentivity labels
     */ static getDefaultSensitivityLabel(sensitivityLabels) {
        if (sensitivityLabels == null || sensitivityLabels.length === 0) {
            return null;
        }
        let retVal = null;
        for (const sensitivityLabel of sensitivityLabels){
            if (sensitivityLabel.isDefault) {
                retVal = sensitivityLabel;
                break;
            } else if (sensitivityLabel.sublabels != null) {
                for (const sublabel of sensitivityLabel.sublabels){
                    if (sublabel.isDefault) {
                        retVal = sublabel;
                        break;
                    }
                }
            }
            if (retVal != null) {
                break;
            }
        }
        return retVal;
    }
    /**
     * Given a list of sensitivity labels, return the one matching the given sensitivity label id.
     * If not found, return null.
     * @param labelId the target sensitivity label's id
     * @param labels sentivity labels
     */ static getSensitivityLabel(labelId, labels) {
        if (isEmpty(labelId) || labels == null || labels.length === 0) {
            return null;
        }
        let retVal = null;
        for (const label of labels){
            if (label.id === labelId) {
                retVal = label;
                break;
            } else if (label.sublabels != null) {
                for (const sublabel of label.sublabels){
                    if (sublabel.id === labelId) {
                        retVal = sublabel;
                        break;
                    }
                }
            }
            if (retVal != null) {
                break;
            }
        }
        return retVal;
    }
    /**
     * Given a list of sensitivity labels, return the parent sensitivity label that has the given sensitivity label id as child.
     * If not found, return null.
     * @param sublabelId the target sensitivity sublabel's id
     * @param labels sentivity labels
     */ static getParentSensitivityLabel(sublabelId, labels) {
        if (isEmpty(sublabelId) || labels == null || labels.length === 0) {
            return null;
        }
        let retVal = null;
        for (const label of labels){
            if (label.sublabels != null) {
                for (const sublabel of label.sublabels){
                    if (sublabel.id === sublabelId) {
                        retVal = label;
                        break;
                    }
                }
            }
            if (retVal != null) {
                break;
            }
        }
        return retVal;
    }
    /**
     * Given a list of sensitivity labels and a target sensitivity label id,
     * return the full display name of found sensitivity label.
     * That is, if the found sensitivity label has a parent,
     * return "parentLabel.displayName / childLabel.displayName" format
     * Otherwise, just return displayName.
     * If not found, return empty string.
     * @param labelId the target sensitivity label's id
     * @param labels sentivity labels
     */ static getFullSensitivityTitle(labelId, labels) {
        if (isEmpty(labelId) || isEmpty(labels)) {
            return "";
        }
        const label = SensitivityLabel.getSensitivityLabel(labelId, labels);
        const parentLabel = SensitivityLabel.getParentSensitivityLabel(labelId, labels);
        return label && parentLabel ? StringHelper.format(Strings.SensitivityLabel_FullTitle, parentLabel.displayName, label.displayName) : label?.displayName ?? "";
    }
    /**
     * Initializes a new instance of sensitivity label.
     * @param propertyBag Sensitivity label property bag.
     */ constructor(propertyBag){
        this.id = propertyBag.id;
        this.name = propertyBag.name;
        this.displayName = propertyBag.displayName;
        this.description = propertyBag.description;
        this.toolTip = propertyBag.toolTip;
        this.isDefault = propertyBag.isDefault;
        this.isEnabled = propertyBag.isEnabled;
        this.priority = propertyBag.priority;
        this.color = propertyBag.color;
        this.hasProtection = propertyBag.hasProtection;
        this.defaultContentLabel = propertyBag.defaultContentLabel ? new SensitivityLabel(propertyBag.defaultContentLabel) : null;
        this.labelActions = propertyBag.labelActions;
        this.groupAllowGuestUsers = propertyBag.groupAllowGuestUsers;
        this.groupPrivacy = propertyBag.groupPrivacy;
        this.sublabels = null;
        if (propertyBag.sublabels != null) {
            this.sublabels = [];
            propertyBag.sublabels.forEach((sublabel)=>{
                this.sublabels.push(new SensitivityLabel(sublabel));
            });
        }
    }
}
